import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router";
import { QueryProvider } from "@/QueryProvider";
import { Suspense, useEffect, lazy, useRef } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { ErrorProvider } from "@/context/ErrorContext";
import { PermissionProvider } from "@/context/PermissionContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AUTH_CONSTANTS } from "@/constants/auth";
import { initSentry, reportWebVitals } from "@/utils/sentry";
const MyFoodDashboard = lazy(() => import("./pages/Dashboard/MyFoodDashboard"));
const Login = lazy(() => import("./pages/AuthPages/Login"));
const NotFound = lazy(() => import("./pages/OtherPage/NotFound"));
const AppLayout = lazy(() => import("./layout/AppLayout"));
const NewsSync = lazy(() => import("./pages/Admin/NewsSync"));
// Vor-Ort-Anmeldung (MemberRegistration) entfernt 2026-07-11 — Anmeldungen laufen über myfood.care
// Old Employee and Reports pages removed - empty demo pages
// MenuPolicyPage removed - static menu structure used
// All test/development pages removed - production ready
const AdminPortal = lazy(() => import("./pages/Admin/AdminPortal"));
const EmployeePortal = lazy(() => import("./pages/Employee/EmployeePortal"));
const BranchManagement = lazy(() => import("./pages/Admin/BranchManagement"));
const EmployeeManagement = lazy(() => import("./pages/Admin/EmployeeManagement"));
const EmployeePushNotifications = lazy(() => import("./pages/Admin/EmployeePushNotifications"));
const RolesPermissions = lazy(() => import("./pages/Admin/RolesPermissions"));
const WidgetPermissions = lazy(() => import("./pages/Admin/WidgetPermissions"));
const MemberSearch = lazy(() => import("./pages/Members/MemberSearch"));
const MemberDetail = lazy(() => import("./pages/Members/MemberDetail"));
const MemberConversion = lazy(() => import("./pages/Members/MemberConversion"));
const SocialMarketRenewal = lazy(() => import("./pages/Members/SocialMarketRenewal"));
const PovertyThresholdManager = lazy(() => import("./pages/Admin/PovertyThresholdManager"));
const LegalDocumentsManager = lazy(() => import("./pages/Admin/LegalDocumentsManager"));
const DailyReportPage = lazy(() => import("./pages/Reports/DailyReport"));
const MonthlyReportPage = lazy(() => import("./pages/Reports/MonthlyReport"));
const CustomerAttributionPage = lazy(() => import("./pages/Reports/CustomerAttribution"));
const AccountDeletionRequests = lazy(() => import("./pages/Members/AccountDeletionRequests"));
// Hinweis: PushNotifications, NotificationSettings und ContactInquiries
// werden jetzt nur noch indirekt über MessagesHub eingebunden — direkte
// Imports hier wären toter Code. Alte Routen redirecten in App.tsx.
const MessagesHub = lazy(() => import("./pages/Messages/MessagesHub"));
const MyProfile = lazy(() => import("./pages/Profile/MyProfile"));
const HelpSupport = lazy(() => import("./pages/Help/HelpSupport"));
const OnlineRegistrations = lazy(() => import("./pages/Employee/OnlineRegistrations"));
const ProductReservations = lazy(() => import("./pages/Employee/ProductReservations"));
import { ScrollToTop } from "./components/common/ScrollToTop";

/**
 * LocationAwareRoutes - Wrapper um sicherzustellen, dass jede Route mit einem eindeutigen key neu gemountet wird
 * Behebt: Navigation-Bug nach Mitglied-Laden in MemberConversion
 */
function LocationAwareRoutes() {
  const location = useLocation();
  
  // Eindeutiger Key aus pathname + timestamp für vollständige Neu-Mounting-Garantie
  // Dieser Key wird direkt in die Route-Elemente eingebaut um React Router zu zwingen,
  // die Komponenten neu zu mounten
  const routeKey = `${location.pathname}${location.search}${location.hash}`;

  return (
    <Routes key={routeKey}>
          {/* Geschütztes Layout */}
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route index path={AUTH_CONSTANTS.ROUTES.HOME} element={<MyFoodDashboard />} />
            {/* Old Employee and Reports routes removed - empty demo pages */}
            {/* MenuPolicyPage route removed - static menu structure used */}
            {/* All test/development routes removed - production ready */}
            
            {/* Karten Portal */}
            <Route path="/adminportal" element={<ProtectedRoute roles={["admin"]} menuId="karten-portal.administrator"><AdminPortal /></ProtectedRoute>} />
            <Route path="/employee" element={<ProtectedRoute roles={["admin","director","manager","employee"]} menuId="karten-portal.mitarbeiter"><EmployeePortal /></ProtectedRoute>} />
            
            {/* Verwaltung - nur Admin */}
            <Route path="/branches" element={<ProtectedRoute roles={["admin"]} menuId="verwaltung.filialen"><BranchManagement /></ProtectedRoute>} />
            <Route path="/employee-management" element={<ProtectedRoute roles={["admin"]} menuId="verwaltung.mitarbeiter-verwaltung"><EmployeeManagement /></ProtectedRoute>} />
            <Route path="/employee-push" element={<ProtectedRoute roles={["admin"]} menuId="verwaltung.employee-push"><EmployeePushNotifications /></ProtectedRoute>} />
            <Route path="/roles-permissions" element={<ProtectedRoute roles={["admin"]} menuId="verwaltung.rollen-rechte"><RolesPermissions /></ProtectedRoute>} />
            <Route path="/widget-permissions" element={<ProtectedRoute roles={["admin"]} menuId="verwaltung.widget-einstellungen"><WidgetPermissions /></ProtectedRoute>} />
            <Route path="/settings/poverty-threshold" element={<ProtectedRoute roles={["admin"]} menuId="verwaltung.eu-silc"><PovertyThresholdManager /></ProtectedRoute>} />
            <Route path="/settings/legal-documents" element={<ProtectedRoute roles={["admin"]} menuId="verwaltung.rechtliches"><LegalDocumentsManager /></ProtectedRoute>} />
            <Route path="/news-sync" element={<ProtectedRoute roles={["admin"]} menuId="verwaltung.news-sync"><NewsSync /></ProtectedRoute>} />
            
            {/* Online Anmeldungen */}
            <Route path="/online-registrations" element={<ProtectedRoute roles={["admin","director","manager","employee"]} menuId="online-anmeldungen"><OnlineRegistrations /></ProtectedRoute>} />
            <Route path="/product-reservations" element={<ProtectedRoute roles={["admin","director","manager","employee"]} menuId="online-anmeldungen"><ProductReservations /></ProtectedRoute>} />
            
            {/* Mitglieder */}
            <Route path="/member-search" element={<ProtectedRoute roles={["admin","director","manager","employee"]} menuId="mitglieder.suche"><MemberSearch /></ProtectedRoute>} />
            <Route path="/member-detail/:memberId" element={<ProtectedRoute roles={["admin","director","manager","employee"]} menuId="mitglieder.suche"><MemberDetail /></ProtectedRoute>} />
            <Route path="/member-conversion" element={<ProtectedRoute roles={["admin","director","manager"]} menuId="mitglieder.umwandlung"><MemberConversion /></ProtectedRoute>} />
            <Route path="/social-market-renewal" element={<ProtectedRoute roles={["admin","director","manager"]} menuId="mitglieder.verlaengerung"><SocialMarketRenewal /></ProtectedRoute>} />
            
            {/* Berichte - Admin, Geschäftsführung, Teamleitung */}
            <Route path="/reports/daily-report" element={<ProtectedRoute roles={["admin","director","manager"]} menuId="berichte.tagesbericht"><DailyReportPage /></ProtectedRoute>} />
            <Route path="/reports/monthly-report" element={<ProtectedRoute roles={["admin","director","manager"]} menuId="berichte.monatsbericht"><MonthlyReportPage /></ProtectedRoute>} />
            <Route path="/reports/customer-attribution" element={<ProtectedRoute roles={["admin","director","manager"]} menuId="berichte.kundenzuordnung"><CustomerAttributionPage /></ProtectedRoute>} />
            
            {/* Nachrichten-Zentrale (vereint Anfragen + Push-Versand + Einstellungen) */}
            <Route path="/messages" element={<ProtectedRoute roles={["admin","director","manager"]} menuId="mitglieder.inquiries"><MessagesHub /></ProtectedRoute>} />

            {/* Alte Routen → Redirect auf passenden Tab. Wer einen alten
                Bookmark hat, landet sauber im neuen Hub. Keine ProtectedRoute
                davor: die Ziel-Route schützt sich selbst — ein Redirect darf
                nicht an Permissions hängen bleiben (sonst NotFound). */}
            <Route path="/contact-inquiries"     element={<Navigate to="/messages?tab=inbox"         replace />} />
            <Route path="/push-notifications"    element={<Navigate to="/messages?tab=senden"        replace />} />
            <Route path="/notification-settings" element={<Navigate to="/messages?tab=einstellungen" replace />} />

            <Route path="/account-deletion-requests" element={<ProtectedRoute roles={["admin","director","manager"]} menuId="mitglieder.account-deletion"><AccountDeletionRequests /></ProtectedRoute>} />
            
            {/* Stammdaten */}
            <Route path="/my-profile" element={<ProtectedRoute roles={["admin","director","manager","employee"]} menuId="stammdaten.mein-profil"><MyProfile /></ProtectedRoute>} />
            
            {/* Hilfe & Dokumente */}
            <Route path="/hilfe" element={<ProtectedRoute roles={["admin","director","manager","employee"]} menuId="hilfe.support"><HelpSupport /></ProtectedRoute>} />
          </Route>

          {/* Auth */}
          <Route path={AUTH_CONSTANTS.ROUTES.SIGNIN} element={<Login />} />

          {/* Fallback */}
          <Route path="*" element={<NotFound />} />
          </Routes>
  );
}

export default function App() {
  // Sentry initialisieren
  useEffect(() => {
    initSentry();
    reportWebVitals();
  }, []);

  // BFCache handling entfernt - verursachte Reload-Loop

  // Globaler Zurück-Button-Guard: Wenn keine Tokens vorhanden sind, immer zu /signin
  useEffect(() => {
    const onPop = () => {
      const hasToken = !!localStorage.getItem(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS) || !!localStorage.getItem(AUTH_CONSTANTS.TOKEN_KEYS.API);
      if (!hasToken && window.location.pathname !== AUTH_CONSTANTS.ROUTES.SIGNIN) {
        window.history.replaceState(null, '', AUTH_CONSTANTS.ROUTES.SIGNIN);
        window.location.replace(AUTH_CONSTANTS.ROUTES.SIGNIN);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return (
    <QueryProvider>
      <AuthProvider>
        <PermissionProvider>
          <ErrorProvider>
            <Router>
              <ScrollToTop />
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                <LocationAwareRoutes />
          </Suspense>
          </Router>
          </ErrorProvider>
        </PermissionProvider>
      </AuthProvider>
    </QueryProvider>
  );
}
